import { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import type { FHRequest, PendingResponse, RequestDelay } from '../../types';
import { REQUEST_STATUS } from '../../types';
import { mentorApi } from '../../api/mentor_api';
import { requestApi } from '../../api/request_api';
import { aiApi, type AISuggestion } from '../../api/ai_api';
import { getStatusLabel, getStatusColor } from '../StatusChip';
import { useRequestStore, useMentorStore } from '../../stores/root_store';

interface MentorActionsCardProps {
  request: FHRequest;
  currentUserUuid: string;
  onRequestUpdate: () => void;
}

export const MentorActionsCard = observer(function MentorActionsCard({
  request: requestProp,
  currentUserUuid,
  onRequestUpdate,
}: MentorActionsCardProps) {
  const requestStore = useRequestStore();
  const mentorStore = useMentorStore();
  const navigate = useNavigate();

  // Use store's request to ensure we have the latest data after updates
  // Fall back to prop for initial render before store is populated
  const request = requestStore.getRequestByKey(requestProp.key) || requestProp;
  const [claiming, setClaiming] = useState(false);
  const [approving, setApproving] = useState(false);
  const [denying, setDenying] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [showDenyForm, setShowDenyForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pendingResponse, setPendingResponse] = useState<PendingResponse | null>(null);
  const [loadingResponse, setLoadingResponse] = useState(false);
  const [activeDelay, setActiveDelay] = useState<RequestDelay | null>(null);
  const [aiAssessment, setAiAssessment] = useState<AISuggestion | null>(null);

  const navigateToNextTask = useCallback(() => {
    const next = mentorStore.getNextTask(request.key);
    if (next) {
      navigate(`/assistant/request/${next.key}`);
    } else {
      navigate('/assistant/mentor');
    }
  }, [mentorStore, request.key, navigate]);

  useEffect(() => {
    const fetchPendingResponse = async () => {
      setLoadingResponse(true);
      try {
        const response = await mentorApi.getPendingResponse(request.key);
        setPendingResponse(response);
      } catch {
        // Silently fail - response may not exist
        setPendingResponse(null);
      } finally {
        setLoadingResponse(false);
      }
    };
    fetchPendingResponse();
  }, [request.key]);

  // Fetch AI assessment for this request — if none exists the backend
  // queues generation and we poll once after a delay to pick it up
  useEffect(() => {
    if (!request.uuid) return;
    let cancelled = false;

    aiApi.getAssessment(request.uuid)
      .then((res) => {
        if (cancelled) return;
        if (res.assessment) {
          setAiAssessment(res.assessment);
        } else if (res.status === 'queued') {
          // Poll once after a few seconds for the result
          setTimeout(() => {
            if (cancelled) return;
            aiApi.getAssessment(request.uuid)
              .then((retry) => { if (!cancelled) setAiAssessment(retry.assessment); })
              .catch(() => {});
          }, 5000);
        }
      })
      .catch(() => setAiAssessment(null));

    return () => { cancelled = true; };
  }, [request.uuid]);

  // Fallback: fetch active delay for legacy-created delays (no delay_time on BaseResponse)
  useEffect(() => {
    if (
      pendingResponse?.suggested_status === REQUEST_STATUS.AWAITING_THIRD_PARTY &&
      !pendingResponse.delay_time &&
      request.uuid
    ) {
      requestApi.getActiveDelay(request.uuid).then(setActiveDelay).catch(() => setActiveDelay(null));
    } else {
      setActiveDelay(null);
    }
  }, [pendingResponse, request.uuid]);

  const isClaimed = request.mentor_claimed;
  const isClaimedByMe = isClaimed && request.mentor_uuid === currentUserUuid;
  const isClaimedByOther = isClaimed && request.mentor_uuid !== currentUserUuid;
  const isMyOwnTask = request.assistant_uuid === currentUserUuid;

  const handleClaim = async () => {
    setClaiming(true);
    setError(null);
    setSuccess(null);
    try {
      const updatedRequest = await mentorApi.claim(request.key);
      requestStore.receiveRequestUpdate(updatedRequest);
      setSuccess('Claimed for review');
      onRequestUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to claim');
    } finally {
      setClaiming(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    setError(null);
    setSuccess(null);
    try {
      const updatedRequest = await mentorApi.approve(request.key);
      requestStore.receiveRequestUpdate(updatedRequest);
      // Clear pending response since it's now approved
      setPendingResponse(null);
      // Refresh messages since the approved response is now visible
      if (request.key) {
        requestStore.fetchMessages(request.key, true);
      }
      setSuccess('Response approved');
      onRequestUpdate();
      mentorStore.removeFromQueue(request.key);
      navigateToNextTask();
    } catch (err) {
      console.error('[MentorActionsCard] approve error:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const handleDeny = async () => {
    if (!denyReason.trim()) {
      setError('Please provide a reason for denial');
      return;
    }
    setDenying(true);
    setError(null);
    setSuccess(null);
    try {
      const updatedRequest = await mentorApi.deny(request.key, denyReason.trim());
      requestStore.receiveRequestUpdate(updatedRequest);
      // Clear pending response since mentor has taken action
      setPendingResponse(null);
      // Refresh messages since the denial feedback is now visible
      if (request.key) {
        requestStore.fetchMessages(request.key, true);
      }
      setSuccess('Response denied');
      setDenyReason('');
      setShowDenyForm(false);
      onRequestUpdate();
      mentorStore.removeFromQueue(request.key);
      navigateToNextTask();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny');
    } finally {
      setDenying(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <RateReviewIcon color="primary" />
          <Typography variant="h6">Mentor Review</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Pending response being reviewed */}
        {loadingResponse && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loadingResponse && pendingResponse && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: pendingResponse.close_without_response ? 'warning.light' : 'action.hover' }}>
            {pendingResponse.close_without_response ? (
              // Close Without Responding UI
              <>
                <Typography variant="subtitle2" color="warning.contrastText" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Close Without Responding
                </Typography>
                {pendingResponse.author_name && (
                  <Typography variant="caption" color="warning.contrastText" sx={{ display: 'block', mb: 1, opacity: 0.8 }}>
                    From: {pendingResponse.author_name}
                    {pendingResponse.date_created && (
                      <> &middot; {new Date(pendingResponse.date_created).toLocaleString()}</>
                    )}
                  </Typography>
                )}
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Task will close <strong>without notifying the client</strong>.
                </Alert>
              </>
            ) : (
              // Regular response UI
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Response Pending Approval
                </Typography>
                {pendingResponse.author_name && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    From: {pendingResponse.author_name}
                    {pendingResponse.date_created && (
                      <> &middot; {new Date(pendingResponse.date_created).toLocaleString()}</>
                    )}
                  </Typography>
                )}
                {pendingResponse.suggested_status !== null && (
                  <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Status if approved:
                    </Typography>
                    <Chip
                      label={getStatusLabel(pendingResponse.suggested_status)}
                      color={getStatusColor(pendingResponse.suggested_status)}
                      size="small"
                    />
                  </Box>
                )}
                {(pendingResponse.delay_time || activeDelay) && (() => {
                  const delayTimeStr = pendingResponse.delay_time || activeDelay?.scheduled_time;
                  return (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      Delay scheduled for{' '}
                      <strong>
                        {delayTimeStr
                          ? (() => {
                              try {
                                return new Date(delayTimeStr).toLocaleString(undefined, {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                });
                              } catch {
                                return delayTimeStr;
                              }
                            })()
                          : 'a scheduled time'}
                      </strong>.
                    </Alert>
                  );
                })()}
                <Box
                  sx={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  <Typography variant="body2">{pendingResponse.body}</Typography>
                </Box>
              </>
            )}
          </Paper>
        )}

        {/* AI Assessment */}
        {aiAssessment && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <AutoAwesomeIcon sx={{ fontSize: 16 }} color="secondary" />
              <Typography variant="caption" fontWeight="bold" color="secondary.main">
                AI Assessment
              </Typography>
              {aiAssessment.confidence !== null && (
                <Chip
                  label={`${Math.round(aiAssessment.confidence * 100)}% confidence`}
                  size="small"
                  color={aiAssessment.confidence >= 0.8 ? 'success' : aiAssessment.confidence >= 0.5 ? 'warning' : 'error'}
                  sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
              {aiAssessment.content}
            </Typography>
          </Paper>
        )}

        {/* Cannot mentor your own task */}
        {isMyOwnTask && (
          <Alert severity="info">
            You cannot mentor your own tasks.
          </Alert>
        )}

        {/* Not claimed - show claim button */}
        {!isClaimed && !isMyOwnTask && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={claiming}
            onClick={handleClaim}
            startIcon={claiming ? <CircularProgress size={16} /> : <RateReviewIcon />}
          >
            {claiming ? 'Claiming...' : 'Claim for Review'}
          </Button>
        )}

        {/* Claimed by another mentor */}
        {isClaimedByOther && !isMyOwnTask && (
          <Alert severity="info">
            This task is being reviewed by another mentor.
          </Alert>
        )}

        {/* Claimed by current user - show approve/deny */}
        {isClaimedByMe && !isMyOwnTask && (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="contained"
                color={pendingResponse?.close_without_response ? 'warning' : 'success'}
                fullWidth
                disabled={approving || denying}
                onClick={handleApprove}
                startIcon={approving ? <CircularProgress size={16} /> : <CheckIcon />}
              >
                {approving
                  ? 'Approving...'
                  : pendingResponse?.close_without_response
                    ? 'Yes, Close Without Responding'
                    : 'Approve'}
              </Button>
              <Button
                variant="contained"
                color="error"
                fullWidth
                disabled={approving || denying}
                onClick={() => setShowDenyForm(!showDenyForm)}
                startIcon={<CloseIcon />}
              >
                Deny
              </Button>
            </Box>

            {showDenyForm && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Reason for Denial
                </Typography>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Explain why this response needs revision..."
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  disabled={denying || !denyReason.trim()}
                  onClick={handleDeny}
                  startIcon={denying ? <CircularProgress size={16} /> : <CloseIcon />}
                >
                  {denying ? 'Submitting...' : 'Submit Denial'}
                </Button>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
});
